import { type Dispatch, type SetStateAction, useMemo, useState } from "react";
import { useParams } from "@/lib/router";
import { useQuery, useMutation } from "@tanstack/react-query";
import { API_BASE } from "@/lib/api";
import {
  AlertTriangle,
  ArrowLeft,
  BrainCircuit,
  CheckCircle2,
  Clock3,
  Copy,
  CreditCard,
  Download,
  ExternalLink,
  FileInput,
  Layers3,
  Loader2,
  Play,
  ShieldCheck,
  Sparkles,
  TableProperties,
  XCircle,
  type LucideIcon,
} from "lucide-react";
import { Link } from "@/lib/router";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";

const statusLabels: Record<string, string> = {
  production: "Producción",
  beta: "Beta vendible",
  demo: "Demo guiada",
  experimental: "Experimental",
};

const phaseLabels: Record<string, string> = {
  "phase-1": "Fase 1: quick win",
  "phase-2": "Fase 2: procesamiento pesado",
  "phase-3": "Fase 3: compliance",
  "phase-4": "Fase 4: marketplace/API",
};

export function AiServiceView() {
  const { serviceId } = useParams<{ serviceId: string }>();
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [clientError, setClientError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const urlRunToken = typeof window !== "undefined" ? new URLSearchParams(window.location.search).get("run_token") : null;
  const accountToken = typeof window !== "undefined" ? window.localStorage.getItem("ai-suite-account-token") || "" : "";

  const { data: service, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["ai", "services", serviceId],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/services/${serviceId}`);
      if (!res.ok) throw new Error("Failed to load service details");
      return res.json();
    },
  });

  const { data: health } = useQuery({
    queryKey: ["ai", "services", serviceId, "health"],
    enabled: Boolean(serviceId),
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/services/${serviceId}/health`);
      if (!res.ok) return null;
      return res.json();
    },
  });

  const mutation = useMutation({
    mutationFn: async (data: any) => {
      const validationError = validateRequiredInputs(service, data);
      if (validationError) {
        setClientError(validationError);
        throw new Error(validationError);
      }

      setClientError(null);
      const needsPayment = Boolean(service?.billing?.mode?.startsWith("pay_per_run"));
      let payload = { ...data };

      if (needsPayment && !payload.run_token && !payload.runToken && !urlRunToken) {
        const checkoutRes = await fetch(`${API_BASE}/services/${serviceId}/checkout`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(accountToken ? { Authorization: `Bearer ${accountToken}` } : {}),
          },
          body: JSON.stringify({ serviceId }),
        });
        const checkout = await checkoutRes.json().catch(() => ({}));
        if (!checkoutRes.ok) throw new Error(checkout.message || checkout.error || "Checkout failed");
        if (checkout.checkoutUrl) {
          window.location.href = checkout.checkoutUrl;
          return { success: false, result: "Redirigiendo a pago..." };
        }
        payload = { ...payload, run_token: checkout.runToken };
      } else if (urlRunToken && !payload.run_token && !payload.runToken) {
        payload = { ...payload, run_token: urlRunToken };
      }

      const hasFiles = Object.values(payload).some((val) => val instanceof File);
      let body: any;
      let headers: Record<string, string> = {};

      if (hasFiles) {
        body = new FormData();
        Object.entries(payload).forEach(([key, value]) => {
          if (value instanceof File) body.append(key, value);
          else if (value !== undefined && value !== null) body.append(key, String(value));
        });
      } else {
        body = JSON.stringify(payload);
        headers["Content-Type"] = "application/json";
      }
      if (accountToken) headers.Authorization = `Bearer ${accountToken}`;

      const res = await fetch(`${API_BASE}/services/${serviceId}/execute`, {
        method: "POST",
        headers,
        body,
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.message || errData.error || "Execution failed");
      }
      return res.json();
    },
  });

  const previewMutation = useMutation({
    mutationFn: async (data: any) => {
      const validationError = validateRequiredInputs(service, data, true);
      if (validationError) {
        setClientError(validationError);
        throw new Error(validationError);
      }
      setClientError(null);

      const hasFiles = Object.values(data).some((val) => val instanceof File);
      let body: any;
      let headers: Record<string, string> = {};

      if (hasFiles) {
        body = new FormData();
        Object.entries(data).forEach(([key, value]) => {
          if (value instanceof File) body.append(key, value);
          else if (value !== undefined && value !== null) body.append(key, String(value));
        });
      } else {
        body = JSON.stringify(data);
        headers["Content-Type"] = "application/json";
      }

      const res = await fetch(`${API_BASE}/services/${serviceId}/preview`, {
        method: "POST",
        headers,
        body,
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.message || errData.error || "Preview failed");
      }
      return res.json();
    },
  });

  const resultText = useMemo(() => {
    const activeData = mutation.data || previewMutation.data;
    if (!activeData?.result) return "";
    return typeof activeData.result === "string"
      ? activeData.result
      : JSON.stringify(activeData.result, null, 2);
  }, [mutation.data, previewMutation.data]);

  if (isLoading) {
    return (
      <div className="mx-auto max-w-5xl space-y-6">
        <Skeleton className="h-12 w-40 rounded-full bg-white/10" />
        <Skeleton className="h-80 w-full rounded-3xl bg-white/10" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="mx-auto max-w-3xl rounded-[1.5rem] border border-rose-200/25 bg-rose-500/10 p-8 text-rose-50">
        <h1 className="text-2xl font-black">No se pudo cargar el servicio</h1>
        <p className="mt-3 text-sm leading-6 text-rose-100/85">{error.message || "La API no respondió correctamente."}</p>
        <Button className="mt-6 rounded-full bg-white px-6 text-slate-950 hover:bg-rose-100" onClick={() => refetch()}>
          Reintentar
        </Button>
      </div>
    );
  }

  if (!service) return <div>Servicio no encontrado.</div>;

  const servicePhase = getServicePhase(service);
  const visibleUseCases = (service.useCases || []).slice(0, 6);
  const paidLabel = service.paidTier?.label || service.billing?.displayPrice || "Resultado completo";
  const freeLabel = service.freeTier?.enabled ? service.freeTier.label || "Preview gratis" : "Sin preview gratis";
  const disclaimers = serviceDisclaimers(service);

  if (service.launchMode === "external" && service.externalUrl) {
    return (
      <div className="mx-auto max-w-4xl space-y-6">
        <BackButton />
        <Card className="rounded-[2rem] border-white/10 bg-white/[0.055] p-2">
          <CardContent className="space-y-6 p-8">
            <div className="flex flex-wrap gap-2">
              <Badge>Miniapp externa</Badge>
              <Badge>{phaseLabels[servicePhase]}</Badge>
              <Badge>{statusLabels[service.status] || service.status || "Demo"}</Badge>
            </div>
            <div>
              <h1 className="text-4xl font-black">{service.name}</h1>
              <p className="mt-3 max-w-2xl text-slate-300">{service.description}</p>
            </div>
            <ServiceBrief service={service} freeLabel={freeLabel} paidLabel={paidLabel} />
            <ServiceDisclaimer disclaimers={disclaimers} />
            {service.billing?.displayPrice && (
              <div className="rounded-2xl border border-amber-200/20 bg-amber-300/10 p-4 text-sm text-amber-50">
                Pago por uso: <strong>{service.billing.displayPrice}</strong>. El catálogo exige checkout antes de abrir esta miniapp.
              </div>
            )}
            <Button
              className="h-12 rounded-full bg-white px-6 font-bold text-slate-950 hover:bg-cyan-100"
              onClick={async () => {
                setClientError(null);
                try {
                  if (service.billing?.mode?.startsWith("pay_per_run")) {
                    const checkoutRes = await fetch(`${API_BASE}/services/${serviceId}/checkout`, {
                      method: "POST",
                      headers: accountToken ? { Authorization: `Bearer ${accountToken}` } : undefined,
                    });
                    const checkout = await checkoutRes.json().catch(() => ({}));
                    if (!checkoutRes.ok) throw new Error(checkout.message || checkout.error || "Checkout failed");
                    if (checkout.checkoutUrl) {
                      window.location.href = checkout.checkoutUrl;
                      return;
                    }
                    if (checkout.runToken) {
                      const external = new URL(service.externalUrl, window.location.origin);
                      external.searchParams.set("run_token", checkout.runToken);
                      window.location.href = external.toString();
                      return;
                    }
                    throw new Error("Checkout did not return a payment URL or run token");
                  }
                  window.location.href = service.externalUrl;
                } catch (error) {
                  setClientError(error instanceof Error ? error.message : "No se pudo iniciar el checkout");
                }
              }}
            >
              {service.billing?.displayPrice ? `Pagar ${service.billing.displayPrice} y abrir` : "Abrir demo"}
              <ExternalLink className="ml-2 h-4 w-4" />
            </Button>
            {clientError && (
              <div className="rounded-2xl border border-red-300/20 bg-red-500/10 p-3 text-sm text-red-100">{clientError}</div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl space-y-8 pb-16">
      <BackButton />

      <section className="rounded-[2rem] border border-white/10 bg-white/[0.055] p-6 shadow-2xl sm:p-8">
        <div className="grid gap-6 lg:grid-cols-[1fr_340px]">
          <div className="space-y-4">
            <div className="flex flex-wrap gap-2">
              <Badge>{service.category || "AI Service"}</Badge>
              <Badge>{statusLabels[service.status] || service.status || "Demo"}</Badge>
              <Badge>{phaseLabels[servicePhase]}</Badge>
              <Badge>{service.provider || "AI runtime"}</Badge>
            </div>
            <h1 className="text-4xl font-black tracking-tight sm:text-5xl">{service.name}</h1>
            <p className="max-w-3xl text-lg leading-8 text-slate-300">{service.description}</p>
            {visibleUseCases.length > 0 && (
              <div className="flex flex-wrap gap-2 pt-2">
                {visibleUseCases.map((useCase: string) => (
                  <span key={useCase} className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs text-slate-300">
                    {useCase}
                  </span>
                ))}
              </div>
            )}
          </div>
          <div className="rounded-3xl border border-white/10 bg-black/20 p-5">
            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-cyan-200/10 text-cyan-100">
                <ShieldCheck className="h-5 w-5" />
              </div>
              <div>
                <p className="text-sm font-bold">Estado operativo</p>
                <p className="text-sm text-slate-400">{health?.status || "verificando"}</p>
              </div>
            </div>
            <div className="mt-5 grid grid-cols-2 gap-3 text-sm">
              <InfoPill label="Tiempo" value={`${service.estimatedTimeSec || 30}s`} />
              <InfoPill label="Precio" value={service.billing?.displayPrice || "Demo"} />
              <InfoPill label="Inputs" value={`${service.inputs?.length || 0}`} />
              <InfoPill label="Límite" value={service.maxUploadMb ? `${service.maxUploadMb} MB` : "Texto"} />
            </div>
          </div>
        </div>
      </section>

      <ServiceDisclaimer disclaimers={disclaimers} />

      {urlRunToken && (
        <div className="rounded-[1.5rem] border border-emerald-200/25 bg-emerald-500/10 p-5 text-emerald-50">
          <div className="flex items-center gap-2 text-sm font-black">
            <CheckCircle2 className="h-4 w-4" />
            Run token recibido
          </div>
          <p className="mt-2 text-sm leading-6 text-emerald-100/85">
            El checkout volvió con token de ejecución. Completa los campos y ejecuta el resultado completo sin cambiar el caso si el token fue emitido para este run.
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[420px_1fr]">
        <Card className="h-fit rounded-[1.5rem] border-white/10 bg-white/[0.055]">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-cyan-200" />
              Configurar demo
            </CardTitle>
            <CardDescription>Completa los campos y genera un entregable listo para mostrar.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            <ServiceBrief service={service} freeLabel={freeLabel} paidLabel={paidLabel} />
            {service.billing?.displayPrice && (
              <div className="rounded-2xl border border-amber-200/20 bg-amber-300/10 p-4 text-sm leading-6 text-amber-50">
                <p className="font-bold">Pago por uso: {service.billing.displayPrice}</p>
                <p className="text-amber-100/80">Gratis: {service.freeTier?.label || "preview limitado"}. Completo: {service.paidTier?.label || "resultado completo"}.</p>
              </div>
            )}
            {(service.inputs || []).length === 0 && (
              <div className="rounded-2xl border border-amber-200/20 bg-amber-300/10 p-4 text-sm leading-6 text-amber-50">
                Este servicio no expone formulario interno. Si es una miniapp externa, usa el CTA de apertura; si debería ejecutar aquí, falta definir inputs en catálogo.
              </div>
            )}

            {(service.inputs || []).map((input: any) => (
              <div key={input.name} className="space-y-2">
                <Label htmlFor={input.name} className="font-semibold">
                  {input.label} {input.required && <span className="text-red-300">*</span>}
                </Label>

                {input.type === "textarea" ? (
                  <Textarea
                    id={input.name}
                    placeholder={input.placeholder}
                    value={formData[input.name] || ""}
                    onChange={(e) => handleInputChange(input.name, e.target.value, setFormData)}
                    className="min-h-[170px] border-white/10 bg-black/20 focus:ring-cyan-200/20"
                  />
                ) : input.type === "select" ? (
                  <Select
                    value={formData[input.name] || input.default}
                    onValueChange={(val) => handleInputChange(input.name, val, setFormData)}
                  >
                    <SelectTrigger className="w-full border-white/10 bg-black/20">
                      <SelectValue placeholder="Selecciona" />
                    </SelectTrigger>
                    <SelectContent>
                      {input.options?.map((opt: string) => (
                        <SelectItem key={opt} value={opt}>
                          {opt}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    id={input.name}
                    type={input.type}
                    accept={input.accept}
                    placeholder={input.placeholder}
                    className="border-white/10 bg-black/20"
                    onChange={(e) => {
                      const val = input.type === "file" ? e.target.files?.[0] : e.target.value;
                      handleInputChange(input.name, val, setFormData);
                    }}
                  />
                )}
              </div>
            ))}

            {clientError && (
              <div className="rounded-2xl border border-red-300/20 bg-red-500/10 p-3 text-sm text-red-100">{clientError}</div>
            )}

            <div className="grid gap-3">
              {service.freeTier?.enabled && (
                <Button
                  variant="outline"
                  className="h-12 w-full rounded-2xl border-cyan-200/30 bg-cyan-200/10 text-base font-black text-cyan-50 hover:bg-cyan-200/20"
                  onClick={() => previewMutation.mutate(formData)}
                  disabled={previewMutation.isPending || mutation.isPending}
                >
                  {previewMutation.isPending ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : <Sparkles className="mr-2 h-5 w-5" />}
                  Probar gratis
                </Button>
              )}
              <Button
                className="h-12 w-full rounded-2xl bg-cyan-200 text-base font-black text-slate-950 hover:bg-white"
                onClick={() => mutation.mutate(formData)}
                disabled={mutation.isPending || previewMutation.isPending}
              >
                {mutation.isPending ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : <Play className="mr-2 h-5 w-5 fill-current" />}
                {service.billing?.displayPrice ? `Pagar ${service.billing.displayPrice} y desbloquear completo` : "Ejecutar demo"}
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card className="min-h-[560px] overflow-hidden rounded-[1.5rem] border-white/10 bg-white/[0.045]">
          <CardHeader className="border-b border-white/10 bg-black/20">
            <CardTitle className="flex items-center justify-between">
              <span>{service.outputLabel || "Resultado"}</span>
              {(mutation.isPending || previewMutation.isPending) && <Clock3 className="h-5 w-5 text-cyan-200" />}
              {(mutation.isSuccess || previewMutation.isSuccess) && <CheckCircle2 className="h-5 w-5 text-emerald-300" />}
              {(mutation.isError || previewMutation.isError) && <XCircle className="h-5 w-5 text-red-300" />}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {mutation.isPending || previewMutation.isPending ? (
              <ProcessingState />
            ) : mutation.isSuccess || previewMutation.isSuccess ? (
              mutation.data?.result?.type === "files" ? (
                <FilesResult files={mutation.data.result.files} />
              ) : (
                <TextResult text={resultText} copied={copied} setCopied={setCopied} serviceName={service.name} />
              )
            ) : mutation.isError || previewMutation.isError ? (
              <div className="p-6">
                <div className="rounded-2xl border border-red-300/20 bg-red-500/10 p-5 text-sm text-red-100">
                  Error al procesar: {mutation.error?.message || previewMutation.error?.message}
                </div>
              </div>
            ) : (
              <EmptyState service={service} />
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function BackButton() {
  return (
    <Button variant="ghost" asChild className="rounded-full text-slate-300 hover:bg-white/10 hover:text-white">
      <Link to="..">
        <ArrowLeft className="mr-2 h-4 w-4" />
        Volver al catálogo
      </Link>
    </Button>
  );
}

function Badge({ children }: { children: string }) {
  return <span className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs font-semibold text-slate-300">{children}</span>;
}

function ServiceDisclaimer({ disclaimers }: { disclaimers: string[] }) {
  if (!disclaimers.length) return null;
  return (
    <section className="rounded-[1.5rem] border border-amber-200/25 bg-amber-500/10 p-5 text-amber-50">
      <div className="flex items-center gap-2 text-sm font-black">
        <AlertTriangle className="h-4 w-4" />
        Disclaimer de uso responsable
      </div>
      <div className="mt-3 grid gap-2 md:grid-cols-2">
        {disclaimers.slice(0, 4).map((item) => (
          <p key={item} className="rounded-2xl border border-amber-200/15 bg-black/20 p-3 text-sm leading-6 text-amber-50/90">
            {item}
          </p>
        ))}
      </div>
    </section>
  );
}

function InfoPill({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/5 p-3">
      <p className="text-xs uppercase tracking-[0.16em] text-slate-500">{label}</p>
      <p className="mt-1 font-bold text-white">{value}</p>
    </div>
  );
}

function ServiceBrief({ service, freeLabel, paidLabel }: { service: any; freeLabel: string; paidLabel: string }) {
  return (
    <div className="grid gap-3 sm:grid-cols-3">
      <BriefPill icon={FileInput} label="Entrada" value={formatInputs(service)} />
      <BriefPill icon={TableProperties} label="Salida" value={service.outputLabel || "Entregable IA"} />
      <BriefPill icon={CreditCard} label="Modelo" value={service.billing?.displayPrice ? `Pay-per-run ${service.billing.displayPrice}` : "Demo"} />
      <div className="rounded-2xl border border-white/10 bg-black/20 p-4 sm:col-span-3">
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-slate-500">
          <Layers3 className="h-4 w-4" />
          Tiers comerciales
        </div>
        <div className="mt-3 grid gap-3 md:grid-cols-2">
          <div className="rounded-xl border border-cyan-200/15 bg-cyan-200/10 p-3">
            <p className="text-xs font-bold text-cyan-100">Preview</p>
            <p className="mt-1 text-sm text-slate-200">{freeLabel}</p>
          </div>
          <div className="rounded-xl border border-amber-200/15 bg-amber-200/10 p-3">
            <p className="text-xs font-bold text-amber-100">Completo</p>
            <p className="mt-1 text-sm text-slate-200">{paidLabel}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function BriefPill({ icon: Icon, label, value }: { icon: LucideIcon; label: string; value: string }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-black/20 p-4">
      <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-slate-500">
        <Icon className="h-4 w-4" />
        {label}
      </div>
      <p className="mt-2 text-sm font-bold text-white">{value}</p>
    </div>
  );
}

function ProcessingState() {
  return (
    <div className="flex min-h-[520px] flex-col items-center justify-center gap-5 p-10 text-center">
      <div className="relative">
        <div className="absolute inset-0 rounded-full bg-cyan-300/20 blur-3xl" />
        <Loader2 className="relative h-16 w-16 animate-spin text-cyan-200" />
      </div>
      <div>
        <p className="text-xl font-black">Procesando demo</p>
        <p className="mt-2 max-w-md text-sm leading-6 text-slate-400">Estamos generando el entregable. Mantén esta ventana abierta.</p>
      </div>
    </div>
  );
}

function EmptyState({ service }: { service: any }) {
  return (
    <div className="flex min-h-[520px] flex-col items-center justify-center gap-5 p-10 text-center">
      <BrainCircuit className="h-20 w-20 text-cyan-200/60" />
      <div>
        <p className="text-xl font-black">Listo para ejecutar</p>
        <p className="mt-2 max-w-md text-sm leading-6 text-slate-400">
          {service.businessValue || "Completa la configuración para generar un resultado demostrable."}
        </p>
      </div>
    </div>
  );
}

function FilesResult({ files }: { files: Array<{ name: string; content?: string; downloadUrl?: string; mime?: string; size?: number }> }) {
  return (
    <div className="p-6">
      <div className="rounded-3xl border border-emerald-300/20 bg-emerald-500/10 p-6">
        <div className="flex items-center gap-2 text-emerald-200">
          <CheckCircle2 className="h-5 w-5" />
          <p className="font-bold">Reportes generados correctamente</p>
        </div>
        <div className="mt-5 space-y-3">
          {files.map((file) => (
            <Button key={file.name} className="h-12 w-full justify-start rounded-2xl bg-white text-slate-950 hover:bg-cyan-100" onClick={() => downloadFile(file)}>
              <Download className="mr-2 h-5 w-5" />
              Descargar {file.name}{file.size ? ` (${formatBytes(file.size)})` : ""}
            </Button>
          ))}
        </div>
      </div>
    </div>
  );
}

function TextResult({ text, copied, setCopied, serviceName }: { text: string; copied: boolean; setCopied: (value: boolean) => void; serviceName: string }) {
  return (
    <div className="space-y-4 p-6">
      <div className="max-h-[560px] min-h-[420px] overflow-auto rounded-3xl border border-white/10 bg-black/30 p-6 text-sm leading-7 text-slate-100">
        <pre className="whitespace-pre-wrap font-sans">{text}</pre>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <Button
          variant="outline"
          className="h-11 rounded-2xl border-white/10 bg-white/5"
          onClick={async () => {
            await navigator.clipboard.writeText(text);
            setCopied(true);
            window.setTimeout(() => setCopied(false), 1600);
          }}
        >
          <Copy className="mr-2 h-4 w-4" />
          {copied ? "Copiado" : "Copiar"}
        </Button>
        <Button className="h-11 rounded-2xl bg-white text-slate-950 hover:bg-cyan-100" onClick={() => downloadText(`${serviceName.replace(/\s+/g, "_")}.txt`, text, "text/plain;charset=utf-8")}>
          <Download className="mr-2 h-4 w-4" />
          Descargar .txt
        </Button>
      </div>
    </div>
  );
}

function handleInputChange(name: string, value: any, setFormData: Dispatch<SetStateAction<Record<string, any>>>) {
  setFormData((prev) => ({ ...prev, [name]: value }));
}

function validateRequiredInputs(service: any, data: Record<string, any>, allowPreview = false) {
  for (const input of service.inputs || []) {
    if (allowPreview && (input.name === "run_token" || input.name === "runToken")) continue;
    if (!input.required) continue;
    const value = data[input.name];
    if (value === undefined || value === null || value === "") {
      return `Falta completar: ${input.label}`;
    }
  }
  return null;
}

function formatInputs(service: any) {
  const inputs = service.inputs || [];
  if (inputs.length === 0) return service.launchMode === "external" ? "En miniapp" : "Sin formulario";
  const fileInputs = inputs.filter((input: any) => input.type === "file").length;
  if (fileInputs > 0) return `${inputs.length} campos, ${fileInputs} archivo(s)`;
  return `${inputs.length} campos`;
}

function getServicePhase(service: any) {
  const explicitPhase = service.phase ?? service.roadmapPhase ?? service.catalogPhase;
  const normalized = String(explicitPhase || "").toLowerCase();
  if (normalized.includes("4")) return "phase-4";
  if (normalized.includes("3")) return "phase-3";
  if (normalized.includes("2")) return "phase-2";
  if (normalized.includes("1")) return "phase-1";

  const text = [service.name, service.category, service.description, service.provider, ...(service.tags || []), ...(service.useCases || [])]
    .join(" ")
    .toLowerCase();

  if (text.match(/medical|salud|instagram|whatsapp automation|scraping|scraper|lead data|compliance|legal/)) return "phase-3";
  if (text.match(/audio|imagen|image|ocr|pdf|docx|xlsx|csv|gis|geodatabase|transcrip|archivo|upload/)) return "phase-2";
  if (service.launchMode === "external") return "phase-2";
  return "phase-1";
}

function serviceDisclaimers(service: any) {
  const text = [
    service.id,
    service.name,
    service.category,
    service.description,
    ...(service.tags || []),
    ...(service.useCases || []),
  ].join(" ").toLowerCase();
  const disclaimers = new Set<string>(service.disclaimers || []);
  if (/(legal|contrato|contract|derecho|petition|traffic|multa|comparendo|compliance|medical|medgemma|salud|wellness|screening|tenant|arriendo|tax|invoice|audit|finance|factura)/i.test(text)) {
    disclaimers.add("Orientación informativa. No constituye asesoría profesional definitiva; valida con un profesional o autoridad competente antes de tomar decisiones sensibles.");
  }
  if (/(medical|medgemma|salud|symptom|medication|clinical|clinica|clínica)/i.test(text)) {
    disclaimers.add("No diagnostica ni prescribe. En emergencias, síntomas graves o decisiones clínicas contacta servicios médicos o un profesional de salud.");
  }
  if (/(legal|derecho|traffic|multa|comparendo|contract|petition|pqrs|reclamo)/i.test(text)) {
    disclaimers.add("Los borradores legales deben revisarse según jurisdicción, fechas, evidencia y norma vigente antes de enviarse o radicarse.");
  }
  if (/(invoice|receipt|tax|audit|finance|factura|contabilidad)/i.test(text)) {
    disclaimers.add("Los cálculos y extracciones financieras son borradores operativos; no sustituyen contabilidad certificada ni asesoría tributaria.");
  }
  return Array.from(disclaimers);
}

function downloadText(name: string, content: string, type: string) {
  const blob = new Blob([content], { type });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
}

function downloadFile(file: { name: string; content?: string; downloadUrl?: string; mime?: string }) {
  if (file.downloadUrl) {
    const a = document.createElement("a");
    a.href = file.downloadUrl;
    a.download = file.name;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    return;
  }
  downloadText(file.name, file.content || "", file.mime || "text/plain;charset=utf-8");
}

function formatBytes(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
